if (!customElements.get('tp-instalment-plans')) {
    class TpInstalmentPlans extends HTMLElement {
        constructor() {
            super();
            this.activeInstalments = 0;
            this.isBuilder = false;
            this._initialized = false;
            this._listenersAttached = false;
            this._observer = null;
            this._onDocumentClick = this.handleDocumentClick.bind(this);
            this._onPriceUpdate = this.handlePriceUpdate.bind(this);
        }

        connectedCallback() {
            if (this._initialized) return;
            this._initialized = true;
            this.onInit();
        }

        disconnectedCallback() {
            this.removeEventListeners();
            this.disconnectObserver();
            this._initialized = false;
        }

        get selectors() {
            return {
                container: '[js-instalment-plans="container"]',
                klarna: '[js-instalment-plans="klarna"]',
                klarnaContent: '[js-instalment-plans="klarnaContent"]',
                clearpay: '[js-instalment-plans="clearpay"]',
                clearpayContent: '[js-instalment-plans="clearpayContent"]',
                paypal: '[js-instalment-plans="paypal"]',
                paypalContent: '[js-instalment-plans="paypalContent"]',
                deko: '[js-instalment-plans="deko"]',
                dekoContent: '[js-instalment-plans="dekoContent"]',
                toggle: '[js-instalment-plans="toggle"]',
                closeDrawer: 'instalment-plans-drawer__close'
            };
        }

        onInit() {
            this.isBuilder = !!document.querySelector('.product-builder-total-price');
            this.setEventListeners();
            const price = this.getInitialPrice();
            this.update(price);
        }

        getInitialPrice() {
            const totalCart = document.querySelector('[js-cart="total"]');
            const productPrice = document.querySelector('.product-price');
            const total = totalCart ? totalCart.getAttribute('data-price') : null;
            const product = productPrice ? productPrice.getAttribute('data-price') : null;
            return total ? total : product;
        }

        disableScroll() {
            setTimeout(() => {
                if (!document.body.hasAttribute('data-body-scroll-fix')) {
                    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                    document.body.setAttribute('data-body-scroll-fix', scrollPosition);
                    document.body.style.overflow = 'hidden';
                    document.body.style.position = 'fixed';
                    document.body.scrollTo(0, scrollPosition);
                    document.body.style.top = `-${scrollPosition}px`;
                    document.body.style.left = '0';
                    document.body.style.width = '100%';
                }
            }, 15);
        }

        enableScroll() {
            if (document.body.hasAttribute('data-body-scroll-fix')) {
                const scrollPosition = document.body.getAttribute('data-body-scroll-fix');
                document.body.removeAttribute('data-body-scroll-fix');
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.width = '';
                window.scroll(0, scrollPosition);
            }
        }

        setEventListeners() {
            if (this._listenersAttached) return;

            document.addEventListener('click', this._onDocumentClick);
            document.addEventListener('update::price', this._onPriceUpdate);

            if (this.isBuilder) {
                const targetNode = document.getElementById('builderProductPrice');
                if (targetNode) {
                    this._observer = new MutationObserver((mutationList) => {
                        for (const mutation of mutationList) {
                            if (mutation.type === 'attributes' && mutation.attributeName === 'data-price') {
                                const productPrice = document.querySelector('.product-price');
                                const price = productPrice ? productPrice.getAttribute('data-price') : null;
                                this.update(price);
                            }
                        }
                    });
                    this._observer.observe(targetNode, { attributes: true });
                }
            }

            this._listenersAttached = true;
        }

        removeEventListeners() {
            if (!this._listenersAttached) return;
            document.removeEventListener('click', this._onDocumentClick);
            document.removeEventListener('update::price', this._onPriceUpdate);
            this._listenersAttached = false;
        }

        disconnectObserver() {
            if (this._observer) {
                this._observer.disconnect();
                this._observer = null;
            }
        }

        handleDocumentClick({ target }) {
            const isToggler = target.getAttribute('js-instalment-plans') === 'toggle';
            let isCloseDrawer = target.classList.contains('icon-close-small');
            let isCloseOverlay = target.classList.contains('instalment-plans-drawer--overlay');

            if (target.localName == 'path') {
                const svg = target.closest('svg');
                if (svg) {
                    isCloseDrawer = svg.classList.contains('icon-close-small');
                }
            }

            if (isToggler) {
                const drawerId = target.getAttribute('data-target');
                const drawer = document.getElementById(drawerId);
                if (drawer) {
                    drawer.style.display = 'block';
                    drawer.classList.add('is-active');
                    this.disableScroll();
                }
            }

            if (isCloseDrawer) {
                const button = target.closest('button');
                if (!button) return;
                const drawerId = button.getAttribute('data-target');
                const drawer = document.getElementById(drawerId);
                if (drawer) {
                    drawer.style.display = 'none';
                    drawer.classList.remove('is-active');
                    this.enableScroll();
                }
            }

            if (isCloseOverlay) {
                const drawerId = target.getAttribute('data-target');
                const drawer = document.getElementById(drawerId);
                if (drawer) {
                    drawer.style.display = 'none';
                    drawer.classList.remove('is-active');
                    this.enableScroll();
                }
            }
        }

        handlePriceUpdate() {
            const total = document.querySelector('[js-cart="total"]');
            if (!total) return;
            this.update(total.getAttribute('data-price'));
        }

        formatMoney(cents, format) {
            if (typeof cents === 'string') {
                cents = cents.replace('.', '');
            }
            let value = '';
            const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
            const formatString = format || moneyFormat;

            function formatWithDelimiters(
                number,
                precision = 2,
                thousands = ',',
                decimal = '.'
            ) {
                if (isNaN(number) || number == null) {
                    return 0;
                }

                number = (number / 100.0).toFixed(precision);

                const parts = number.split('.');
                const dollarsAmount = parts[0].replace(
                    /(\d)(?=(\d\d\d)+(?!\d))/g,
                    `$1${thousands}`
                );
                const centsAmount = parts[1] ? decimal + parts[1] : '';

                return dollarsAmount + centsAmount;
            }

            switch (formatString.match(placeholderRegex)[1]) {
                case 'amount':
                    value = formatWithDelimiters(cents, 2);
                    break;
                case 'amount_no_decimals':
                    value = formatWithDelimiters(cents, 0);
                    break;
                case 'amount_with_comma_separator':
                    value = formatWithDelimiters(cents, 2, '.', ',');
                    break;
                case 'amount_no_decimals_with_comma_separator':
                    value = formatWithDelimiters(cents, 0, '.', ',');
                    break;
            }

            return formatString.replace(placeholderRegex, value);
        }

        update(price) {
            this.classList.remove('one', 'two', 'three', 'four');
            this.activeInstalments = 0;

            if (window.cartStrings && window.cartStrings.instalmentPlans) {
                this.updatePlan({ price, instalments: Number(window.cartStrings.instalmentPlans.klarna.instalmentAmount), service: 'klarna' });
                this.updatePlan({ price, instalments: Number(window.cartStrings.instalmentPlans.clearpay.instalmentAmount), service: 'clearpay' });
                this.updatePlan({ price, instalments: Number(window.cartStrings.instalmentPlans.paypal.instalmentAmount), service: 'paypal' });
                this.updatePlan({ price, instalments: Number(window.cartStrings.instalmentPlans.deko.instalmentAmount), service: 'deko' });
            }

            if (this.activeInstalments === 0) {
                const planNodes = this.querySelectorAll('.instalment-plans__plan');
                this.activeInstalments = Math.min(4, planNodes.length);
            }

            if (this.activeInstalments === 1) {
                this.classList.add('one');
            } else if (this.activeInstalments === 2) {
                this.classList.add('two');
            } else if (this.activeInstalments === 3) {
                this.classList.add('three');
            } else if (this.activeInstalments === 4) {
                this.classList.add('four');
            }
        }

        updatePlan({ instalments = 3, price = 0, service }) {
            const nodeSelectors = {
                container: this,
                klarna: this.querySelector(this.selectors.klarna),
                klarnaContent: this.querySelector(this.selectors.klarnaContent),
                clearpay: this.querySelector(this.selectors.clearpay),
                clearpayContent: this.querySelector(this.selectors.clearpayContent),
                paypal: this.querySelector(this.selectors.paypal),
                paypalContent: this.querySelector(this.selectors.paypalContent),
                deko: this.querySelector(this.selectors.deko),
                dekoContent: this.querySelector(this.selectors.dekoContent)
            };

            if (!nodeSelectors[service]) {
                return;
            }
            const hidePrices = nodeSelectors.container.dataset.hidePrices === 'true';
            const aboveMin = price >= Number(window.cartStrings.instalmentPlans[service].min);
            const belowMax = price <= Number(window.cartStrings.instalmentPlans[service].max);
            if (!aboveMin || !belowMax) {
                nodeSelectors[service].classList.remove('is-active');
                return;
            }
            if (this.classList.contains('new')) {
                this.activeInstalments += 1;
            }
            let string = window.cartStrings.instalmentPlansString[`${service}NoPrice`];

            if (!hidePrices) {
                if (this.classList.contains('new')) {
                    const amount = this.formatMoney(price / instalments, window.theme.moneyFormat);
                    const button = `<button
                    class="instalment-plans__button"
                    type="button"
                    data-target="${service}Drawer"
                    js-toggle="${service}Drawer"
                    js-instalment-plans="toggle"
                >
                    <span class="instalment-plans__i"></span>

                    <span class="visually-hidden">
                    ${window.cartStrings.instalmentPlansString[`${service}Information`]}
                    </span>
                </button>`;

                    if (service == 'paypal') {
                        string = window.cartStrings.instalmentPlansString[service].replace('%number%', instalments).replace('%amount%', amount).replace('%button%', button);
                    } else if (service == 'klarna') {
                        string = window.cartStrings.instalmentPlansString[service].replace('%number%', instalments).replace('%amount%', amount).replace('%button%', button);
                    } else if (service == 'deko') {
                        string = window.cartStrings.instalmentPlansString[service].replace('%number%', instalments).replace('%amount%', amount).replace('%button%', button);
                    } else if (service == 'clearpay') {
                        string = window.cartStrings.instalmentPlansString[service].replace('%number%', instalments).replace('%amount%', amount).replace('%button%', button);
                    }
                } else {
                    const amount = this.formatMoney(price / instalments, window.theme.moneyFormat);
                    const clearpayIconBlack = `
                    <svg class="icon icon__clearpay-icon" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 133 26"><path d="M105.506 6.35l-9.067 18.842h-3.756l3.387-7.04L90.75 6.35h3.857l3.42 7.907 3.735-7.907h3.745zM71.005 13.04c0-2.163-1.62-3.83-3.61-3.83-1.99 0-3.612 1.61-3.612 3.83 0 2.196 1.621 3.829 3.611 3.829 1.99 0 3.611-1.667 3.611-3.83zm-10.52 12.152V6.349h3.276v1.735c.984-1.228 2.448-1.982 4.192-1.982 3.59 0 6.406 2.973 6.406 6.904 0 3.93-2.873 6.926-6.484 6.926-1.677 0-3.052-.664-4.013-1.79v7.038h-3.377v.012zM86.132 13.04c0-2.253-1.621-3.83-3.611-3.83-1.99 0-3.612 1.61-3.612 3.83 0 2.196 1.622 3.829 3.612 3.829s3.61-1.588 3.61-3.83zm.022 6.69v-1.735c-.984 1.205-2.448 1.948-4.192 1.948-3.645 0-6.406-2.94-6.406-6.904 0-3.93 2.873-6.926 6.484-6.926 1.7 0 3.13.755 4.114 1.926v-1.69h3.264v13.38h-3.264zM54.605 7.656s.827-1.554 2.873-1.554c.872 0 1.431.304 1.431.304v3.412s-1.23-.766-2.359-.608c-1.13.158-1.845 1.194-1.845 2.59v7.93H51.33V6.35h3.265v1.306h.01zM130.862 5.595l-3.868-2.23L123.07 1.1c-2.594-1.498-5.836.383-5.836 3.401v.507c0 .282.145.54.391.676l1.822 1.047c.503.293 1.129-.078 1.129-.664V4.863a.771.771 0 011.152-.676l3.577 2.072 3.567 2.061a.775.775 0 010 1.34l-3.567 2.062-3.577 2.072a.771.771 0 01-1.152-.676v-.597c0-3.018-3.242-4.91-5.835-3.4l-3.913 2.274-3.869 2.23c-2.604 1.51-2.604 5.305 0 6.814l3.869 2.23 3.924 2.264c2.593 1.497 5.836-.383 5.836-3.402v-.506a.764.764 0 00-.392-.676l-1.822-1.048c-.503-.292-1.129.08-1.129.665v1.205a.771.771 0 01-1.152.676l-3.577-2.073-3.567-2.06a.775.775 0 010-1.34l3.567-2.062 3.577-2.072a.771.771 0 011.152.675v.597c0 3.019 3.242 4.91 5.836 3.402l3.924-2.264 3.868-2.23c2.605-1.532 2.605-5.316-.011-6.825zM13.799 14.74c-.716 3.075-3.343 5.192-6.742 5.192-3.924 0-6.875-2.973-6.875-6.904 0-3.93 2.996-6.926 6.931-6.926 3.32 0 5.914 2.117 6.663 5.135h-3.454c-.615-1.205-1.834-2.004-3.186-2.004-1.968 0-3.611 1.633-3.611 3.795s1.643 3.796 3.61 3.796c1.41 0 2.628-.8 3.21-2.084h3.454zM15.353 19.718V.842h3.342v18.876h-3.342zM23.715 13.918c.235 1.982 1.644 3.097 3.421 3.097 1.409 0 2.493-.664 3.13-1.734h3.422c-.794 2.838-3.32 4.651-6.641 4.651-4.014 0-6.82-2.838-6.82-6.87s2.974-6.949 6.898-6.949c3.958 0 6.82 2.94 6.82 6.949 0 .293-.023.586-.079.856h-10.15zm6.663-2.095c-.235-1.734-1.643-2.782-3.287-2.782-1.643 0-2.996 1.014-3.342 2.782h6.63zM45.885 19.718v-1.734c-.984 1.205-2.438 1.948-4.193 1.948-3.633 0-6.395-2.94-6.395-6.904 0-3.93 2.862-6.926 6.474-6.926 1.699 0 3.13.743 4.114 1.926v-1.69h3.264v13.369h-3.264v.011zm-.023-6.69c0-2.241-1.62-3.83-3.61-3.83-1.99 0-3.612 1.6-3.612 3.83 0 2.196 1.621 3.83 3.611 3.83 1.99 0 3.611-1.577 3.611-3.83z" fill="#000"/></svg>
                `;
                    const clearpayIconWhite = `
                    <svg class="icon icon__clearpay-icon" viewBox="0 0 133 26"><path d="M105.506 6.35l-9.067 18.842h-3.756l3.387-7.04L90.75 6.35h3.857l3.42 7.906 3.735-7.906h3.745zM71.005 13.04c0-2.163-1.62-3.83-3.61-3.83-1.99 0-3.612 1.61-3.612 3.83 0 2.196 1.621 3.829 3.611 3.829 1.99 0 3.611-1.667 3.611-3.83zm-10.52 12.152V6.35h3.276v1.734c.984-1.228 2.448-1.982 4.192-1.982 3.59 0 6.406 2.973 6.406 6.904 0 3.93-2.873 6.926-6.484 6.926-1.677 0-3.052-.664-4.013-1.79v7.038h-3.377v.012zM86.132 13.04c0-2.253-1.621-3.83-3.611-3.83-1.99 0-3.612 1.61-3.612 3.83 0 2.196 1.622 3.829 3.612 3.829s3.61-1.588 3.61-3.83zm.022 6.69v-1.735c-.984 1.205-2.448 1.948-4.192 1.948-3.645 0-6.406-2.94-6.406-6.904 0-3.93 2.873-6.926 6.484-6.926 1.7 0 3.13.755 4.114 1.926v-1.69h3.264v13.38h-3.264zM54.605 7.656s.827-1.554 2.873-1.554c.872 0 1.431.304 1.431.304v3.412s-1.23-.765-2.359-.608c-1.13.158-1.845 1.194-1.845 2.59v7.93H51.33V6.35h3.265v1.306h.01zM130.862 5.595l-3.868-2.23-3.924-2.264c-2.594-1.498-5.836.383-5.836 3.401v.507c0 .282.145.54.391.676l1.822 1.047c.503.293 1.129-.078 1.129-.664V4.863a.771.771 0 011.152-.676l3.577 2.072 3.567 2.061a.775.775 0 010 1.34l-3.567 2.062-3.577 2.072a.771.771 0 01-1.152-.676v-.597c0-3.018-3.242-4.91-5.835-3.4l-3.913 2.274-3.869 2.23c-2.604 1.51-2.604 5.305 0 6.814l3.869 2.23 3.924 2.264c2.593 1.497 5.836-.383 5.836-3.402v-.506a.764.764 0 00-.392-.676l-1.822-1.048c-.503-.292-1.129.08-1.129.665v1.205a.771.771 0 01-1.152.676l-3.577-2.073-3.567-2.06a.775.775 0 010-1.34l3.567-2.062 3.577-2.072a.771.771 0 011.152.675v.598c0 3.018 3.242 4.91 5.836 3.4l3.924-2.263 3.868-2.23c2.605-1.532 2.605-5.316-.011-6.825zM13.799 14.74c-.716 3.075-3.343 5.192-6.742 5.192-3.924 0-6.875-2.973-6.875-6.904 0-3.93 2.996-6.926 6.931-6.926 3.32 0 5.914 2.117 6.663 5.135h-3.454c-.615-1.205-1.834-2.004-3.186-2.004-1.968 0-3.611 1.633-3.611 3.795 0 2.163 1.643 3.796 3.61 3.796 1.41 0 2.628-.8 3.21-2.084h3.454zM15.353 19.718V.842h3.342v18.876h-3.342zM23.715 13.918c.235 1.982 1.644 3.097 3.421 3.097 1.409 0 2.493-.664 3.13-1.734h3.422c-.794 2.838-3.32 4.651-6.641 4.651-4.014 0-6.82-2.838-6.82-6.87s2.974-6.949 6.898-6.949c3.958 0 6.82 2.94 6.82 6.949 0 .293-.023.586-.079.856h-10.15zm6.663-2.095c-.235-1.734-1.643-2.782-3.287-2.782-1.643 0-2.996 1.014-3.342 2.782h6.63zM45.885 19.718v-1.734c-.984 1.205-2.438 1.948-4.193 1.948-3.633 0-6.395-2.94-6.395-6.904 0-3.93 2.862-6.926 6.474-6.926 1.699 0 3.13.743 4.114 1.926v-1.69h3.264v13.369h-3.264v.011zm-.023-6.69c0-2.241-1.62-3.83-3.61-3.83-1.99 0-3.612 1.6-3.612 3.83 0 2.196 1.621 3.83 3.611 3.83 1.99 0 3.611-1.577 3.611-3.83z" fill="#fff"/></svg>
                `;

                    if (service == 'klarna') {
                        string = "Make %number% payments of %amount%. No fees with <strong>Klarna</strong>".replace('%number%', instalments).replace('%amount%', amount).replace(
                            '%clearpayicon%',
                            window.cartStrings.store.brand === 'swingball'
                                ? clearpayIconWhite
                                : clearpayIconBlack
                        );
                    }
                    else if (service == 'clearpay') {
                        string = "Make %number% interest free instalments of %amount% with %clearpayicon%".replace('%number%', instalments).replace('%amount%', amount).replace(
                            '%clearpayicon%',
                            window.cartStrings.store.brand === 'swingball'
                                ? clearpayIconWhite
                                : clearpayIconBlack
                        );
                    }
                }
            }

            if (this.classList.contains('new')) {
                nodeSelectors[service].querySelector('.content-container').innerHTML = `${string}`;
            } else {
                nodeSelectors[service].innerHTML = `
            ${string}

            <button
                class="instalment-plans__button"
                type="button"
                data-target="${service}Drawer"
                js-toggle="${service}Drawer"
                js-instalment-plans="toggle"
            >
                <span class="instalment-plans__i"></span>

                <span class="visually-hidden">
                ${window.cartStrings.instalmentPlansString[`${service}Information`]}
                </span>
            </button>
            `;
                nodeSelectors[service].classList.add('is-active');
            }
        }
    }

    customElements.define('tp-instalment-plans', TpInstalmentPlans);
}